import React from 'react';
import { motion } from 'framer-motion';
import CountUp from 'react-countup';
import TiltedCard from './TiltedCard';
import { getFullHeightAccordionImage } from './fullHeightAccordionImageMapping';



interface LibrarySection {
    id: string;
    title: string;
    items: Array<{
        subTopicName: string;
        youtubeLink: string;
    }>;
}

interface LibraryOverviewProps {
    sections: LibrarySection[];
    onSectionClick: (sectionId: string) => void;
    searchValue?: string;
    onResetSearch?: () => void;
}

// Mobile Card Component
const MobileCard: React.FC<{
    section: LibrarySection;
    order: number;
    onClick: () => void;
    isAnimating?: boolean;
    isFadingOut?: boolean;
}> = ({ section, order, onClick, isAnimating = false, isFadingOut = false }) => (
    <motion.div
        initial={{
            opacity: isFadingOut ? 0 : (isAnimating ? 1 : 0),
            y: isAnimating ? 0 : 20,
        }}
        animate={{
            opacity: isFadingOut ? 0 : 1,
            y: 0,
        }}
        transition={{
            duration: isFadingOut ? 0.5 : 0.3,
            delay: isFadingOut ? 0 : (isAnimating ? 0 : order * 0.05),
        }}
        onClick={onClick}
        className='bg-white dark:bg-gray-800 rounded-lg shadow-sm hover:shadow-md transition-all duration-200 cursor-pointer p-5 border border-gray-200 dark:border-gray-700 active:scale-[0.98] min-h-[80px]'
    >
        <div className='flex items-center justify-between'>
            <div className='flex-1 pr-4'>
                <h3 className='text-lg font-semibold text-darkBlue dark:text-white line-clamp-2 mb-1'>
                    {section.title}
                </h3>
                <p className='text-sm text-gray-600 dark:text-gray-400'>
                    <span className='font-inter-numbers'>{section.items.length}</span> topics
                </p>
            </div>
            <div className='flex-shrink-0'>
                <div className='w-12 h-12 rounded-full bg-darkBlue/10 dark:bg-white/10 flex items-center justify-center border border-darkBlue/20 dark:border-white/20'>
                    <span className='text-sm font-bold text-darkBlue dark:text-white font-inter-numbers'>
                        {order}
                    </span>
                </div>
            </div>
        </div>
    </motion.div>
);

const LibraryOverview: React.FC<LibraryOverviewProps> = ({
    sections,
    onSectionClick,
    searchValue = '',
    onResetSearch
}) => {
    const [isFadingOut, setIsFadingOut] = React.useState<boolean>(false);
    const [isMobile, setIsMobile] = React.useState<boolean>(false);
    const [hasAnimatedBefore, setHasAnimatedBefore] = React.useState<boolean>(false);

    // Calculate total topics count
    const totalTopics = sections.reduce((acc, section) => acc + section.items.length, 0);

    // Check sessionStorage on mount to see if animation has played before
    React.useEffect(() => {
        const hasPlayed = sessionStorage.getItem('libraryCountAnimationPlayed');
        if (hasPlayed === 'true') {
            setHasAnimatedBefore(true);
        }
    }, []);

    // Conditionally animate the number
    const shouldAnimate = !hasAnimatedBefore && !searchValue;

    // Mark animation as played when CountUp completes
    const handleCountUpComplete = () => {
        if (!hasAnimatedBefore && !searchValue) {
            sessionStorage.setItem('libraryCountAnimationPlayed', 'true');
            setHasAnimatedBefore(true);
        }
    };

    // Detect mobile
    React.useEffect(() => {
        const checkMobile = () => {
            setIsMobile(window.innerWidth < 640);
        };

        // Check on mount
        checkMobile();

        // Check on resize
        window.addEventListener('resize', checkMobile);
        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    // Reset isFadingOut when search value changes
    React.useEffect(() => {
        setIsFadingOut(false);
    }, [searchValue]);

    const handleSectionClick = (sectionId: string) => {
        setIsFadingOut(true);
        setTimeout(() => {
            onSectionClick(sectionId);
            // Don't reset isFadingOut here - let the component unmount naturally
        }, 500); // Wait for fade animation to complete
    };

    // Since sections are already filtered in parent component, just use them directly
    const filteredSections = sections;

    // Show search results count if searching
    // The sections already contain only matching items when searching
    const searchResultsCount = searchValue.trim()
        ? filteredSections.reduce((acc, section) => acc + section.items.length, 0)
        : 0;

    // If searching and no results, show no results message
    if (searchValue && filteredSections.length === 0) {
        return (
            <div className='min-h-[50vh] flex items-center justify-center relative'>
                <motion.div
                    initial={{ opacity: 0, y: 50, scale: 0.9 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    transition={{ duration: 0.5, ease: "easeOut" }}
                    className='text-center px-8 py-16 bg-gray-100 dark:bg-gray-800
                             rounded-3xl border border-gray-200 dark:border-gray-700 max-w-2xl mx-4 shadow-xl'
                >
                    {/* Animated icon */}
                    <motion.div
                        animate={{ rotate: [0, 10, -10, 0] }}
                        transition={{ duration: 2, repeat: Infinity, repeatDelay: 1 }}
                        className='mb-8'
                    >
                        <svg className='w-16 h-16 text-gray-400 mx-auto' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                            <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z' />
                        </svg>
                    </motion.div>

                    <h3 className='text-4xl text-darkBlue dark:text-white font-bold mb-4'>No sections found</h3>
                    <p className='text-xl text-gray-600 dark:text-gray-300 mb-8 max-w-md mx-auto'>
                        We couldn't find any sections matching
                        <span className='text-darkBlue dark:text-lightBlue font-semibold'> "{searchValue}"</span>
                    </p>
                    {onResetSearch && (
                        <button
                            className='text-lg font-bold text-darkBlue dark:text-lightBlue cursor-pointer hover:underline transition-all'
                            onClick={onResetSearch}
                        >
                            Reset results
                        </button>
                    )}
                </motion.div>
            </div>
        );
    }

    return (
        <div className='relative'>
            {/* Search Results Header */}
            {searchValue && filteredSections.length > 0 && (
                <motion.div
                    initial={{ opacity: 0, y: -20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className='text-center pb-8 px-4'
                >
                    <h2 className='text-3xl md:text-4xl font-bold text-darkBlue dark:text-white mb-2'>
                        Search Results for
                        <span className='text-lightOrange'> "{searchValue}"</span>
                    </h2>
                    <p className='text-gray-600 dark:text-gray-300'>
                        Found <span className='font-inter-numbers'>{searchResultsCount}</span> topics in <span className='font-inter-numbers'>{filteredSections.length}</span> sections
                    </p>
                </motion.div>
            )}

            {/* Overview Header - Only show when not searching */}
            {!searchValue && (
                <motion.div
                    initial={{ opacity: 0, y: -20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                    className='text-center px-4'
                >
                    <p className='text-2xl text-gray-600 dark:text-gray-300'>
                        <span className='font-semibold text-darkBlue dark:text-lightBlue inline-block font-inter-numbers'>
                            {shouldAnimate ? (
                                <CountUp
                                    start={Math.max(0, totalTopics - 100)}
                                    end={totalTopics}
                                    duration={4}
                                    useEasing={true}
                                    separator=","
                                    onEnd={handleCountUpComplete}
                                />
                            ) : (
                                totalTopics
                            )}
                        </span>{' '}
                        topics organized into{' '}
                        <span className='font-semibold text-lightOrange font-inter-numbers'>
                            {sections.length}
                        </span>{' '}
                        sections
                    </p>
                </motion.div>
            )}

            {/* Grid Container with MainCourses styling */}
            <main className='md:mx-24 md:my-10 m-8 pb-14'>
                {isMobile ? (
                    // Mobile Layout - Single column list
                    <div className='flex flex-col gap-3 bg-gray-50 dark:bg-gray-900/50 p-4 rounded-xl -mx-4'>
                        {filteredSections.map((section, index) => (
                            <MobileCard
                                key={section.id}
                                section={section}
                                order={index + 1}
                                onClick={() => handleSectionClick(section.id)}
                                isAnimating={searchValue !== ''}
                                isFadingOut={isFadingOut}
                            />
                        ))}
                    </div>
                ) : (
                    // Desktop/Tablet Layout - Grid
                    <div
                        className='gap-10 grid xl:grid-cols-4 lg:grid-cols-2 gap-y-16'
                    >
                        {filteredSections.map((section, index) => (
                            <motion.div
                                key={section.id}
                                initial={{
                                    opacity: isFadingOut ? 0 : (searchValue ? 1 : 0),
                                    translateX: searchValue ? 0 : (index % 2 === 0 ? -50 : 50),
                                    translateY: searchValue ? 0 : -50,
                                }}
                                animate={{
                                    opacity: isFadingOut ? 0 : 1,
                                    translateX: 0,
                                    translateY: 0,
                                }}
                                transition={{
                                    duration: isFadingOut ? 0.5 : searchValue ? 0 : 0.3,
                                    ease: "easeOut",
                                    delay: isFadingOut || searchValue ? 0 : index * 0.15
                                }}
                                className='justify-self-center'
                            >
                                <TiltedCard
                                    imageSrc={getFullHeightAccordionImage(section.title)}
                                    name={section.title}
                                    order={index + 1}
                                    onClick={() => handleSectionClick(section.id)}
                                    captionText={`${section.items.length} topics`}
                                    containerHeight='420px'
                                    containerWidth='360px'
                                    scaleOnHover={1.05}
                                    rotateAmplitude={10}
                                    showTooltip={false}
                                    displayOverlayContent={true}
                                    overlayContent={
                                        <div className='absolute bottom-20 left-0 right-0 text-center px-3'>
                                            <p className='text-white/90 text-sm font-medium bg-black/50 backdrop-blur-sm rounded-lg py-1 px-3 inline-block'>
                                                <span className='font-inter-numbers'>{section.items.length}</span> topics
                                            </p>
                                        </div>
                                    }
                                />
                            </motion.div>
                        ))}
                    </div>
                )}
            </main>
        </div>
    );
};

export default LibraryOverview; 